import { useState, useEffect } from 'react'
import { Image, Film, FileText, Loader2, Download, Maximize2, XCircle, Trash2 } from 'lucide-react'
import { pedApi } from '@/services/api'

// ── Asset icon helper ──
export function AssetIcon({ mimeType, className }: { mimeType: string; className?: string }) {
  if (mimeType.startsWith('image/')) return <Image className={className} />
  if (mimeType.startsWith('video/')) return <Film className={className} />
  return <FileText className={className} />
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export interface AssetPreviewAsset {
  id: string
  filename: string
  mimeType: string
  size?: number
}

export interface AssetPreviewProps {
  asset: AssetPreviewAsset
  /** Se presenti, mostra il bottone "Elimina" (usato in modifica allegati). */
  onDelete?: (id: string) => void
  deleting?: boolean
  /** Altezza massima dell'area di anteprima. Default identico al comportamento storico. */
  maxHeightClass?: string
  /** Se passato, il click sull'immagine chiama questa funzione invece di (oltre a) aprire il lightbox interno — utile per riusare un modal fullscreen esterno già esistente. */
  onClick?: () => void
  className?: string
}

// ── Single asset preview (streams file through backend proxy as blob URL) ──
export function AssetPreview({ asset, onDelete, deleting, maxHeightClass = 'max-h-64', onClick, className }: AssetPreviewProps) {
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    let cancelled = false
    let blobUrl: string | null = null
    ;(async () => {
      try {
        const res = await pedApi.streamAssetUrl(asset.id)
        const blob = res instanceof Blob ? res : (res as any)?.data instanceof Blob ? (res as any).data : null
        if (!cancelled && blob) {
          blobUrl = URL.createObjectURL(blob)
          setUrl(blobUrl)
        }
      } catch {
        // fallback to presigned URL
        try {
          const res2 = await pedApi.getAssetUrl(asset.id)
          const presigned = (res2 as any)?.data?.url ?? (res2 as any)?.data?.data?.url
          if (!cancelled && presigned) setUrl(presigned)
        } catch { /* silent */ }
      }
      if (!cancelled) setLoading(false)
    })()
    return () => {
      cancelled = true
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [asset.id])

  const isImage = asset.mimeType.startsWith('image/')
  const isVideo = asset.mimeType.startsWith('video/')
  const isPdf = asset.mimeType === 'application/pdf'

  function handleDownload() {
    if (!url) return
    const a = document.createElement('a')
    a.href = url
    a.download = asset.filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  function handleDelete() {
    if (!onDelete) return
    if (!window.confirm('Eliminare questo allegato?')) return
    onDelete(asset.id)
  }

  function handleImageClick() {
    if (onClick) onClick()
    else setExpanded(true)
  }

  return (
    <>
      <div className={className ?? 'rounded-lg border border-slate-200 overflow-hidden bg-white group'}>
        {/* Preview area */}
        <div className="relative bg-slate-50">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
            </div>
          ) : url && isImage ? (
            <button type="button" onClick={handleImageClick} className="w-full cursor-zoom-in">
              <img
                src={url}
                alt={asset.filename}
                className={`w-full ${maxHeightClass} object-contain`}
                loading="lazy"
              />
            </button>
          ) : url && isVideo ? (
            <video
              src={url}
              controls
              preload="metadata"
              className={`w-full ${maxHeightClass}`}
            />
          ) : url && isPdf ? (
            <iframe
              src={url}
              title={asset.filename}
              className="w-full h-52 border-0"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-32 gap-2">
              <FileText className="w-8 h-8 text-slate-300" />
              <span className="text-xs text-slate-400">Anteprima non disponibile</span>
            </div>
          )}
        </div>

        {/* Info bar */}
        <div className="flex items-center gap-2 px-3 py-2 border-t border-slate-200">
          <AssetIcon mimeType={asset.mimeType} className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="flex-1 text-xs text-slate-700 font-medium truncate" title={asset.filename}>
            {asset.filename}
          </span>
          {asset.size != null && (
            <span className="text-[10px] text-slate-400 flex-shrink-0">
              {formatFileSize(asset.size)}
            </span>
          )}
          {url && (
            <button
              type="button"
              onClick={handleDownload}
              className="p-1 rounded text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
              title="Apri in nuova tab"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          )}
          {isImage && url && (
            <button
              type="button"
              onClick={handleImageClick}
              className="p-1 rounded text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
              title="Espandi"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="p-1 rounded text-slate-300 hover:text-danger-600 hover:bg-danger-50 transition-colors opacity-0 group-hover:opacity-100"
              title="Elimina"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Fullscreen lightbox for images (solo se non è stato passato un onClick esterno) */}
      {!onClick && expanded && url && isImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center animate-fade-in"
          onClick={() => setExpanded(false)}
        >
          <button
            onClick={() => setExpanded(false)}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors"
          >
            <XCircle className="w-8 h-8" />
          </button>
          <img
            src={url}
            alt={asset.filename}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-modal"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  )
}
